// Habits list-view column visibility. Account-scoped when a
// PreferencesProvider is mounted, per-browser localStorage otherwise. Only the
// fallback path writes that key: under the provider it is left frozen as the
// one-time migration source, and PreferencesContext keeps its own consolidated
// paint cache.
// Mirrors useHabitStatsRangeSettings: a Record<key, boolean>
// merged over an all-visible default, lazy useState + a single persistence
// useEffect. Only the *hideable* columns are managed here — the Habit
// (identity), Actions, and bulk-select columns always render, so there is no
// "at least one visible" invariant to enforce.
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePreferences } from '../contexts/PreferencesContext';

export const HABIT_COLUMNS_STORAGE_KEY = 'kalpa-habits-columns';

export type ColumnVisibility = Record<string, boolean>;

const buildDefault = (columnKeys: string[]): ColumnVisibility =>
  columnKeys.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as ColumnVisibility);

// Merge stored booleans over the all-visible default for the known keys. Unknown
// or removed stored keys are ignored, and any known key missing from storage
// stays visible — so columns added in future code default visible. Never throws:
// corrupt or inaccessible storage falls back to all-visible.
export const loadColumnVisibility = (
  columnKeys: string[],
  stored?: Record<string, unknown> | null,
): ColumnVisibility => {
  const defaults = buildDefault(columnKeys);
  try {
    let parsed: Partial<Record<string, unknown>>;
    if (stored !== undefined) {
      if (!stored) return defaults;
      parsed = stored;
    } else {
      const raw = localStorage.getItem(HABIT_COLUMNS_STORAGE_KEY);
      if (!raw) return defaults;
      parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    }
    columnKeys.forEach((key) => {
      if (typeof parsed[key] === 'boolean') {
        defaults[key] = parsed[key] as boolean;
      }
    });
    return defaults;
  } catch {
    return defaults;
  }
};

export const useHabitColumnPreferences = (columnKeys: string[]) => {
  const store = usePreferences();
  const [localVisibility, setLocalVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(columnKeys),
  );

  const visibility = useMemo(() => {
    if (!store) return localVisibility;
    return loadColumnVisibility(
      columnKeys,
      store.settings.habits_columns as Record<string, unknown>,
    );
  }, [store, localVisibility, columnKeys]);

  const setVisibility = useCallback(
    (updater: (prev: ColumnVisibility) => ColumnVisibility) => {
      const next = updater(visibility);
      setLocalVisibility(next);
      if (store) {
        store.setNamespace('habits_columns', next);
      }
    },
    [visibility, store],
  );

  useEffect(() => {
    // Providerless fallback only — see the note in useNavPreferences.
    if (store) return;
    try {
      localStorage.setItem(
        HABIT_COLUMNS_STORAGE_KEY,
        JSON.stringify(visibility),
      );
    } catch {
      // Ignore storage failures; the in-memory selection still works this session.
    }
  }, [visibility, store]);

  // Unknown keys default visible (undefined !== false), so a column that only
  // exists in code — not yet in the persisted map — still renders.
  const isColumnVisible = useCallback(
    (key: string) => visibility[key] !== false,
    [visibility],
  );

  const toggleColumn = useCallback(
    (key: string) => {
      setVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
    },
    [setVisibility],
  );

  const resetToDefault = useCallback(() => {
    setLocalVisibility(buildDefault(columnKeys));
    if (store) {
      store.resetNamespace('habits_columns');
    }
  }, [columnKeys, store]);

  return { visibility, isColumnVisible, toggleColumn, resetToDefault };
};

export default useHabitColumnPreferences;
